import React, { useState, Suspense } from 'react';
import { useDeployments } from '../../hooks/useDeployments';
import { ShaderGradient, ShaderGradientCanvas } from '@shadergradient/react';
import TelegramPlatformModal from '../Modals/TelegramPlatformModal';
import WhatsAppPlatformModal from '../Modals/WhatsAppPlatformModal';
import './DeploymentRow.css';

/**
 * DeploymentRow
 * 
 * Thin layout shell for a single deployment row.
 * Delegates config editing to <DeploymentConfigEditor /> and
 * platform modals to their respective components.
 */
const DeploymentRow = ({ deployment, onUpdate }) => {
  const { deleteDeployment } = useDeployments();
  const [updating, setUpdating] = useState(false);
  const [activePlatformModal, setActivePlatformModal] = useState(null);

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this deployment? This cannot be undone.')) {
      return;
    }
    setUpdating(true);
    try {
      await deleteDeployment(deployment.id);
      onUpdate();
    } catch (e) {
      console.error(e);
      setUpdating(false);
      alert(e.message || 'Failed to delete');
    }
  };

  return (
    <div className={`deployment-row ${updating ? 'updating' : ''}`}>
      {/* Icon + Name */}
      <div className="cell name-cell">
        <div className="shader-icon-mini">
          <Suspense fallback={null}>
            <ShaderGradientCanvas
              style={{ position: "absolute", inset: 0 }}
              pixelDensity={1}
              fov={110}
            >
              <ShaderGradient
                control="props"
                type="sphere"
                animate="on"
                brightness={1}
                cAzimuthAngle={0}
                cDistance={14}
                cPolarAngle={140}
                cameraZoom={4.99}
                color1="#ff0000"
                color2="#ff6000"
                color3="#ffffff"
                envPreset="dawn"
                grain="off"
                lightType="env"
                positionX={0}
                positionY={0}
                positionZ={0}
                range="disabled"
                rangeEnd={40}
                rangeStart={0}
                reflection={0}
                rotationX={0}
                rotationY={0}
                rotationZ={0}
                shader="defaults"
                uAmplitude={0}
                uDensity={10}
                uFrequency={5.5}
                uSpeed={0.1}
                uStrength={1}
                uTime={0}
                wireframe={false}
                zoomOut={false}
              />
            </ShaderGradientCanvas>
          </Suspense>
        </div>
        <span className="agent-name">{deployment.name || deployment.agentName || 'AI Agent'}</span>
      </div>

      <div className="cell bundle-cell">
        <span className="bundle-badge">{deployment.agentName}</span>
      </div>

      {/* Provider Info */}
      <div className="cell provider-cell">
        <span className="provider-label">{deployment.providerName}</span>
        <span className={`mode-tag ${deployment.apiMode}`}>
          {deployment.apiMode === 'ready_api' ? 'Ready' : 'Own Key'}
        </span>
      </div>

      {/* Status */}
      <div className="cell">
        <div className={`status-pill ${deployment.status}`}>
          {deployment.status.charAt(0).toUpperCase() + deployment.status.slice(1)}
        </div>
      </div>

      {/* Platform Integration Actions */}
      <div className="cell">
        <select 
          className="row-select platform-select"
          value={activePlatformModal || 'none'}
          onChange={(e) => {
            const val = e.target.value;
            if (val !== 'none') setActivePlatformModal(val);
          }}
        >
          <option value="none" disabled>Manage Channels</option>
          <option value="telegram">
            {deployment.connections?.telegram 
              ? (deployment.connections.telegram.error ? 'Reconnect Telegram ⚠️' : 'Disconnect Telegram') 
              : 'Connect Telegram'}
          </option>
          <option value="whatsapp">
            {deployment.connections?.whatsapp 
              ? (deployment.connections.whatsapp.error ? 'Reconnect WhatsApp ⚠️' : 'Disconnect WhatsApp') 
              : 'Connect WhatsApp'}
          </option>
        </select>
      </div>

      {/* Actions */}
      <div className="cell action" style={{ display: 'flex', gap: '8px' }}>
        <button className="settings-icon-btn delete-btn" title="Delete Deployment" onClick={handleDelete}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ff4b4b" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 6h18"></path>
            <path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"></path>
            <path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"></path>
          </svg>
        </button>
        <button 
          className="settings-icon-btn" 
          title="Settings"
          onClick={() => {
            if (activePlatformModal) {
              setActivePlatformModal(null);
              setTimeout(() => setActivePlatformModal(deployment.platform_type || activePlatformModal), 10);
            } else if (deployment.platform_type) {
              setActivePlatformModal(deployment.platform_type);
            } else {
              alert('Please select a platform (Telegram or WhatsApp) first.');
            }
          }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1V11a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09a1.65 1.65 0 0 0-1.51 1z"/>
          </svg>
        </button>
      </div>

      {/* Platform Modals */}
      {activePlatformModal === 'telegram' && (
        <TelegramPlatformModal 
          deployment={deployment} 
          onClose={() => setActivePlatformModal(null)} 
        />
      )}
      {activePlatformModal === 'whatsapp' && (
        <WhatsAppPlatformModal 
          deployment={deployment} 
          onClose={() => setActivePlatformModal(null)} 
        />
      )}
    </div>
  );
};

export default DeploymentRow;
